import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck as faCircleCheckSolid, faEllipsis, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons';
import { DarkTooltip } from '@framework/ui/atoms/Tooltip';
import type { ModelCommentThreadOperation } from '../../adapter';
import type { UserPublicProfile } from '@user/PublicProfile';

type Props = {
  width: number;
  height: number;
  isResolved: boolean;
  threadOperation: ModelCommentThreadOperation;
  currentUserProfile: UserPublicProfile;
  onClose(): void;
  onEllipsisClick(): void;
};

export const ModelCommentThreadHeader: React.FC<Props> = ({
  width,
  height,
  isResolved,
  threadOperation,
  currentUserProfile,
  onClose,
  onEllipsisClick,
}: Props) => {
  const { t } = useTranslation();

  const handleToggleResolve = useCallback(() => {
    if (isResolved) {
      threadOperation.reopen(currentUserProfile.id).then();
    } else {
      threadOperation.resolve(currentUserProfile.id).then();
    }
  }, [isResolved, threadOperation, currentUserProfile]);

  return (
    <foreignObject width={width} height={height}>
      <div className="flex size-full items-center justify-between rounded-t-2xl border-b-2 border-gray-400 bg-white px-6">
        <span className="text-3xl font-bold text-gray-700">{t('canvas.comment.thread.title')}</span>
        <div className="flex items-center gap-4">
          <button
            type="button"
            aria-label={t('canvas.comment.menu.open')}
            className="flex size-10 items-center justify-center rounded text-3xl text-gray-700 hover:bg-gray-300"
            onClick={onEllipsisClick}
          >
            <FontAwesomeIcon icon={faEllipsis} aria-hidden />
          </button>
          <DarkTooltip
            tooltip={isResolved ? t('canvas.comment.thread.reopenTooltip') : t('canvas.comment.thread.resolveTooltip')}
            placement="bottom"
          >
            <button
              type="button"
              aria-label={
                isResolved ? t('canvas.comment.thread.reopenTooltip') : t('canvas.comment.thread.resolveTooltip')
              }
              className={`flex size-10 items-center justify-center rounded text-3xl hover:bg-gray-300 ${isResolved ? 'text-green-500' : 'text-gray-700'}`}
              onClick={handleToggleResolve}
            >
              <FontAwesomeIcon icon={isResolved ? faCircleCheckSolid : faCircleCheck} />
            </button>
          </DarkTooltip>
          <button
            type="button"
            aria-label={t('common.action.close')}
            className="flex size-10 items-center justify-center rounded text-3xl text-gray-700 hover:bg-gray-300"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden />
          </button>
        </div>
      </div>
    </foreignObject>
  );
};
